// Copyright (c) 2019, 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import equal from "fast-deep-equal";
import * as Firebase from "firebase";
import * as FirebaseUI from "firebaseui";

import { logIfErrorA } from "../log";
import {
  lazy,
  clearArray,
  debounce,
  Action,
  throwIfNull,
  createOuts,
  firstNonNull,
} from "../util";
import { query, queryData, saveWithHistory } from "./ApiUtil";
import {
  Activity,
  ActivityDocument,
  AdministratorDocument,
  CategoryDocument,
  CoordinatorDocument,
  OrganizationDocument,
  Organization,
  SchoolDocument,
  Supervisor,
  SupervisorDocument,
  Volunteer,
  VolunteerDocument,
  MarkdownDocument,
  activityDoc,
  organizationDoc,
  supervisorDoc,
  volunteerDoc,
  VolunteerImportDocument,
  ActivityReviewDocument,
  activityReviewDoc,
  ReviewState,
  Uid,
  UserInfoDocument,
  userInfoDoc,
  ActivityReview,
  Category,
  activityData,
  organizationData,
  supervisorData,
} from "./schema";
import { FIELD_PATH, accessors } from "./FirestoreLayout";
import { logCrash } from "./crash";

const firebase = (global as any).firebase as typeof Firebase;
const firebaseui = (global as any).firebaseui as typeof FirebaseUI;

type AuthUI = firebaseui.auth.AuthUI;

export type User = firebase.User;

const {
  userInfo,
  administrators,
  volunteer,
  school,
  schools,
  schoolAct,
  schoolActs,
  schoolActRvw,
  schoolActRvws,
  schoolCats,
  schoolMdowns,
  schoolOrg,
  schoolOrgs,
  schoolSuper,
  schoolSupers,
  schoolVols,
  schoolVolImport,
  schoolVolImports,
  volActs,
  volOrgs,
  volSupers,
} = accessors(firebase.firestore());

const { error, warn, info, debug } = createOuts("Api", true);

export class Api {
  readonly coordinator: CoordinatorApi;
  readonly volunteer: VolunteerApi;
  readonly getAuthUI: () => AuthUI;

  constructor() {
    this.coordinator = new CoordinatorApi(this);
    this.volunteer = new VolunteerApi(this);
    this.getAuthUI = lazy(() => new firebaseui.auth.AuthUI(firebase.auth()));
  }

  signOut(): Promise<void> {
    return firebase.auth().signOut();
  }

  subscribeAuthChanges(listener: (u: User | null) => void): () => void {
    // Firebase unsubscribes asynchronously, and ReactJS is unhappy
    // getting a callback after unsubscribing.  This blocks any async
    // callbacks that occur after unsubscribe.
    let subscribed = true;
    const forward = (u: User | null) => subscribed && listener(u);
    const unsubscribe = firebase.auth().onAuthStateChanged(forward);
    return () => {
      subscribed = false;
      unsubscribe();
    };
  }

  attachAuthUI(
    containerId: string,
    signInSuccessUrl: string | boolean,
    link: boolean
  ): void {
    const uiConfig = {
      signInSuccessUrl,
      signInOptions: [
        {
          provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
          ...(link
            ? {
                signInMethod:
                  firebase.auth.EmailAuthProvider.EMAIL_LINK_SIGN_IN_METHOD,
              }
            : {}),
          requireDisplayName: false,
          forceSameDevice: false,
        },
      ],
    };
    this.getAuthUI().start(containerId, uiConfig as any);
  }

  async loadSchools(): Promise<SchoolDocument[]> {
    return query("loadSchools", schools().orderBy(FIELD_PATH.dataName));
  }

  async loadUserInfo(user: User): Promise<UserInfoDocument> {
    const email = throwIfNull(user.email);
    const info = await logIfErrorA("GetUserInfo", () => userInfo(email).get());

    if (info != null && info.exists) {
      return info.data() as UserInfoDocument;
    } else {
      return userInfoDoc({
        email,
        allowUnverified: false,
      });
    }
  }
}

class CoordinatorApi {
  private readonly api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  saveActivityReview(review: ActivityReview): Promise<void> {
    return logIfErrorA("saveActivityReview", () =>
      saveWithHistory(
        schoolActRvw(review.schoolUid, review.uid),
        activityReviewDoc(review)
      )
    );
  }
}

class VolunteerApi {
  private readonly api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  saveActivity(activity: Activity): Promise<void> {
    return logIfErrorA("saveActivity", () =>
      saveWithHistory(
        schoolAct(activity.schoolUid, activity.uid),
        activityDoc(activity)
      )
    );
  }

  saveOrganization(org: Organization): Promise<void> {
    return logIfErrorA("saveOrganization", () =>
      saveWithHistory(schoolOrg(org.schoolUid, org.uid), organizationDoc(org))
    );
  }

  saveSupervisor(sup: Supervisor): Promise<void> {
    return logIfErrorA("saveSupervisor", () =>
      saveWithHistory(schoolSuper(sup.schoolUid, sup.uid), supervisorDoc(sup))
    );
  }

  saveVolunteer(v: Volunteer): Promise<void> {
    return logIfErrorA("saveVolunteer", () =>
      saveWithHistory(volunteer(v.uid), volunteerDoc(v))
    );
  }
}

type Unsubscribe = () => any;
type StateListener<T> = (state: T) => void;

class StateCache<T> {
  protected state: T;
  private listeners: StateListener<T>[] = [];
  private notifyAll: Action;

  constructor(empty: T) {
    this.state = empty;
    this.notifyAll = debounce(() => {
      debug("updating debounced listener");
      this.listeners.forEach(listener => listener(this.state));
    });
  }

  public get(): T {
    return this.state;
  }

  public subscribe(listener: StateListener<T>): Unsubscribe {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.findIndex(x => x === listener);
      if (index >= 0) {
        this.listeners.splice(index, 1);
      } else {
        warn("unsubscribe (not found)");
      }
    };
  }

  update(tag: string, update: object) {
    info(`cache update ${tag}`, update);
    const state = { ...this.state, ...update };
    this.state = state;
    if (this.listeners.length !== 1) {
      info(`  calling ${this.listeners.length} listeners`);
    }
    this.notifyAll();
  }
}

function callAndClear(uns: Unsubscribe[]) {
  uns.forEach(x => x());
  clearArray(uns);
}

export interface VolunteerState {
  user: User | null;
  userInfo: UserInfoDocument | null;
  volunteer: VolunteerDocument | null;
  volunteerLoaded: boolean;

  activities: ActivityDocument[];
  activitiesLoaded: boolean;
  activityReviews: ActivityReviewDocument[];
  administrators: AdministratorDocument[];
  categories: CategoryDocument[];
  categoriesLoaded: boolean;
  // coordinators: CoordinatorDocument[];
  import1: VolunteerImportDocument | null;
  markdowns: MarkdownDocument[];
  markdownsLoaded: boolean;
  organizations: OrganizationDocument[];
  organizationsLoaded: boolean;
  school: SchoolDocument | null;
  supervisors: SupervisorDocument[];
  supervisorsLoaded: boolean;

  loaded: boolean;
}

export const EMPTY_VOLUNTEER_STATE: VolunteerState = {
  user: null,
  userInfo: null,
  volunteer: null,
  volunteerLoaded: false,

  activities: [],
  activitiesLoaded: false,
  activityReviews: [],
  administrators: [],
  categories: [],
  categoriesLoaded: false,
  // coordinators: [],
  import1: null,
  markdowns: [],
  markdownsLoaded: false,
  organizations: [],
  organizationsLoaded: false,
  school: null,
  supervisors: [],
  supervisorsLoaded: false,

  loaded: false,
};

function calculateVolunteerCacheLoaded(state: VolunteerState) {
  return (
    state.activitiesLoaded &&
    state.categoriesLoaded &&
    state.markdownsLoaded &&
    state.organizationsLoaded &&
    state.supervisorsLoaded
  );
}

class VolunteerCache extends StateCache<VolunteerState> {
  private actSubs: Unsubscribe[] = [];
  private userSubs: Unsubscribe[] = [];
  private schoolSubs: Unsubscribe[] = [];
  private volSubs: Unsubscribe[] = [];
  private importedUserUid = "invalid uid";
  private readonly debouncedCheckImports: Action;

  constructor() {
    super(EMPTY_VOLUNTEER_STATE);
    this.start();
    this.debouncedCheckImports = debounce(() => this.checkImports(), 3000);
  }

  public close() {
    warn("close");
    callAndClear(this.actSubs);
    callAndClear(this.schoolSubs);
    callAndClear(this.userSubs);
    callAndClear(this.volSubs);
  }

  private start() {
    debug("VolunteerCache.start");
    getApi().subscribeAuthChanges((user: User | null) => {
      info(`auth change '${user && user.uid}'`);
      if (!equal(user, this.state.user)) {
        debug("authed");
        this.update("user", { loaded: false, user });
        user != null && this.listenUser(user);
      }
    });
  }

  private listenUser(user: User) {
    debug(`VolunteerCache.listenUser(${user.uid})`);
    callAndClear(this.userSubs);
    const vol = volunteer(user.uid).onSnapshot(snap => {
      debug(`volunteer update`);
      const volunteer = snap.data() as VolunteerDocument;
      this.update("volunteer", { volunteer, volunteerLoaded: true });
      volunteer != null && this.listenVolunteer(volunteer.data);
    });
    this.userSubs = [vol];
  }

  private listenVolunteer(volunteer: Volunteer) {
    const { schoolUid } = volunteer;
    debug(`VolunteerCache.listenVolunteer schoolUid=${schoolUid}`);
    if (schoolUid !== "") {
      callAndClear(this.volSubs);

      const sch = school(schoolUid).onSnapshot(snap => {
        debug(`school update`);
        const school = snap.data() as SchoolDocument;
        this.update("school", { school });
        // school != null && this.listenSchool(volunteer, school.data);
      });

      const imp1 = schoolVolImport(schoolUid, volunteer.email).onSnapshot(
        snap => {
          debug(`import update`);
          const import1 = snap.data() as VolunteerImportDocument;
          this.update("import1", { import1 });
        }
      );

      const cats = schoolCats(schoolUid).onSnapshot(snap =>
        this.update("cats", {
          categories: queryData(snap),
          categoriesLoaded: true,
        })
      );
      const marks = schoolMdowns(schoolUid).onSnapshot(snap =>
        this.update("marks", {
          markdowns: queryData(snap),
          markdownsLoaded: true,
        })
      );
      const admins = administrators().onSnapshot(snap =>
        this.update("admins", { administrators: queryData(snap) })
      );

      const acts = volActs(volunteer).onSnapshot(snap => {
        const activities = queryData(snap) as ActivityDocument[];
        this.listenActs(volunteer, activities);
        this.update("acts", { activities, activitiesLoaded: true });
      });
      const orgs = volOrgs(volunteer).onSnapshot(snap =>
        this.update("orgs", {
          organizations: queryData(snap),
          organizationsLoaded: true,
        })
      );
      const sups = volSupers(volunteer).onSnapshot(snap =>
        this.update("sups", {
          supervisors: queryData(snap),
          supervisorsLoaded: true,
        })
      );

      this.volSubs = [sch, imp1, cats, marks, acts, admins, orgs, sups];
    } else {
      debug("VolunteerCache: not subscribing to empty school");
    }
  }

  private listenActs(volunteer: Volunteer, activities: ActivityDocument[]) {
    const { email, schoolUid } = volunteer;
    debug(`listenActivities school=${schoolUid} volunteer=${email}`);
    callAndClear(this.actSubs);
    this.update("areviews", { activityReviews: [] });
    this.actSubs = activities.map(actDoc => {
      const { uid } = actDoc.data;
      debug(`listenActs subscribing review '${uid}`);
      return schoolActRvw(schoolUid, uid).onSnapshot(snap => {
        const review =
          (snap.data() as ActivityReviewDocument) ||
          defaultActivityReview(schoolUid, uid);

        debug(
          `listenActs updating review for '${uid}' as ${review.data.state}`
        );
        const others = this.state.activityReviews.filter(
          x => x.data.uid !== uid
        );
        this.update("areviews", { activityReviews: [...others, review] });
      });
    });
  }

  update(tag: string, update: object) {
    const loaded = calculateVolunteerCacheLoaded({ ...this.state, ...update });
    super.update(tag, { ...update, loaded });
    this.debouncedCheckImports();
  }

  private async checkImports() {
    const {
      activities,
      activitiesLoaded,
      organizations,
      organizationsLoaded,
      supervisors,
      supervisorsLoaded,
      import1: import1Doc,
      volunteer: volunteerDoc,
    } = this.state;
    if (volunteerDoc == null) {
      return;
    }
    const { schoolUid, uid: volunteerUid } = volunteerDoc.data;
    if (
      !activitiesLoaded ||
      !organizationsLoaded ||
      !supervisorsLoaded ||
      import1Doc == null ||
      schoolUid == null ||
      this.importedUserUid === volunteerUid
    ) {
      return;
    }

    const import1 = import1Doc.data;

    for (let org of import1.organizations) {
      if (!organizations.some(x => x.data.uid === org.uid)) {
        debug(`checkImports did not see organization '${org.name}', copying`);
        await schoolOrg(schoolUid, org.uid).set(
          organizationDoc({ ...organizationData(org), volunteerUid })
        );
      }
    }
    for (let sup of import1.supervisors) {
      if (!supervisors.some(x => x.data.uid === sup.uid)) {
        debug(`checkImports did not see supervisor '${sup.name}', copying`);
        await schoolSuper(schoolUid, sup.uid).set(
          supervisorDoc({ ...supervisorData(sup), volunteerUid })
        );
      }
    }
    for (let act of import1.activities) {
      if (!activities.some(x => x.data.uid === act.uid)) {
        debug(`checkImports did not see activity '${act.uid}', copying`);
        await schoolAct(schoolUid, act.uid).set(
          activityDoc({ ...activityData(act), volunteerUid })
        );
      }
    }
    this.importedUserUid = volunteerUid;
  }
}

export interface CoordinatorStateUpdate {
  user?: User | null;

  loaded?: boolean;

  coordinator?: CoordinatorDocument | null;

  administrators?: AdministratorDocument[];
  categories?: CategoryDocument[];
  categoriesLoaded?: boolean;
  activities?: ActivityDocument[];
  activitiesLoaded?: boolean;
  activityReviews?: ActivityReviewDocument[];
  activityReviewsLoaded?: boolean;
  organizations?: OrganizationDocument[];
  organizationsLoaded?: boolean;
  school?: SchoolDocument | null;
  supervisors?: SupervisorDocument[];
  supervisorsLoaded?: boolean;
  volunteers?: VolunteerDocument[];
  volunteersLoaded?: boolean;
  volunteerImports?: VolunteerImportDocument[];
  volunteerImportsLoaded?: boolean;

  actIndex?: Map<Uid, Activity>;
  actRvwIndex?: Map<Uid, ActivityReview>;
  catIndex?: Map<Uid, Category>;
  orgIndex?: Map<Uid, Organization>;
  supIndex?: Map<Uid, Supervisor>;
  volIndex?: Map<Uid, Volunteer>;
}

export interface CoordinatorState {
  user: User | null;

  loaded: boolean;

  userInfo: UserInfoDocument | null;
  coordinator: CoordinatorDocument | null;

  administrators: AdministratorDocument[];
  categories: CategoryDocument[];
  categoriesLoaded: boolean;
  activities: ActivityDocument[];
  activitiesLoaded: boolean;
  activityReviews: ActivityReviewDocument[];
  activityReviewsLoaded: boolean;
  organizations: OrganizationDocument[];
  organizationsLoaded: boolean;
  school: SchoolDocument | null;
  supervisors: SupervisorDocument[];
  supervisorsLoaded: boolean;
  volunteers: VolunteerDocument[];
  volunteersLoaded: boolean;
  volunteerImports: VolunteerImportDocument[];
  volunteerImportsLoaded: boolean;

  actIndex: Map<Uid, Activity>;
  actRvwIndex: Map<Uid, ActivityReview>;
  catIndex: Map<Uid, Category>;
  orgIndex: Map<Uid, Organization>;
  supIndex: Map<Uid, Supervisor>;
  volIndex: Map<Uid, Volunteer>;
}

export const EMPTY_COORDINATOR_STATE: CoordinatorState = {
  user: null,
  loaded: false,
  userInfo: null,
  coordinator: null,
  administrators: [],
  categories: [],
  categoriesLoaded: false,
  activities: [],
  activitiesLoaded: false,
  activityReviews: [],
  activityReviewsLoaded: false,
  organizations: [],
  organizationsLoaded: false,
  school: null,
  supervisors: [],
  supervisorsLoaded: false,
  volunteers: [],
  volunteersLoaded: false,
  volunteerImports: [],
  volunteerImportsLoaded: false,

  actIndex: new Map(),
  actRvwIndex: new Map(),
  catIndex: new Map(),
  orgIndex: new Map(),
  supIndex: new Map(),
  volIndex: new Map(),
};

function calculateCoordinatorCacheLoaded(state: CoordinatorState) {
  const result =
    state.categoriesLoaded &&
    state.activitiesLoaded &&
    state.activityReviewsLoaded &&
    state.organizationsLoaded &&
    state.supervisorsLoaded &&
    state.volunteersLoaded &&
    state.volunteerImportsLoaded;
  debug(`Coordinator loaded = ${result}`, state);
  return result;
}

class CoordinatorCache extends StateCache<CoordinatorState> {
  private userSubscriptions: Unsubscribe[] = [];
  private schoolSubscriptions: Unsubscribe[] = [];

  constructor() {
    super(EMPTY_COORDINATOR_STATE);
    this.start();
  }

  public close() {
    warn("CoordinatorCache.close");
    callAndClear(this.schoolSubscriptions);
    callAndClear(this.userSubscriptions);
  }

  private start() {
    debug("CoordinatorCache.start");
    getApi().subscribeAuthChanges((user: User | null) => {
      info(`auth change '${user && user.uid}'`);
      if (!equal(user, this.state.user)) {
        debug("authed");
        this.update("user", { loaded: false, user });
        user != null && this.listenUser(user);
      }
    });
  }

  private listenUser(user: User) {
    debug(`CoordinatorCache.listenUser(${user.uid}, email=${user.email})`);
    callAndClear(this.userSubscriptions);
    const { uid, email } = user;

    if (email == null) {
      crash("CoordinatorCache.listenUser: no email", { uid });
    }
    const uinf = userInfo(email).onSnapshot(snap => {
      const userInfo = snap.data() as UserInfoDocument;
      this.update("userInfo", { userInfo });
      if (userInfo == null || userInfo.data.coordinatorRole == null) {
        crash(`CoordinatorCache.listenUser not coordinator`, { uid, userInfo });
      }
      this.listenSchool(userInfo.data.coordinatorRole.schoolUid);
    });
    const admins = administrators().onSnapshot(snap =>
      this.update("admins", { administrators: queryData(snap) })
    );
    this.userSubscriptions = [admins, uinf];
  }

  private listenSchool(schoolUid: Uid) {
    debug(`CoordinatorCache.listenCoordinator schoolUId=${schoolUid}`);
    callAndClear(this.schoolSubscriptions);
    const sch = school(schoolUid).onSnapshot(snap => {
      const school = snap.data() as SchoolDocument;
      this.update("school", { school });
    });
    const acts = schoolActs(schoolUid).onSnapshot(snap =>
      this.update("acts", {
        activities: queryData(snap),
        activitiesLoaded: true,
      })
    );
    const actRvws = schoolActRvws(schoolUid).onSnapshot(snap =>
      this.update("actRvws", {
        activityReviews: queryData(snap),
        activityReviewsLoaded: true,
      })
    );
    const cats = schoolCats(schoolUid).onSnapshot(snap =>
      this.update("cats", {
        categories: queryData(snap),
        categoriesLoaded: true,
      })
    );
    const orgs = schoolOrgs(schoolUid).onSnapshot(snap =>
      this.update("orgs", {
        organizations: queryData(snap),
        organizationsLoaded: true,
      })
    );
    const sups = schoolSupers(schoolUid).onSnapshot(snap =>
      this.update("sups", {
        supervisors: queryData(snap),
        supervisorsLoaded: true,
      })
    );
    const vols = schoolVols(schoolUid).onSnapshot(snap =>
      this.update("vols", {
        volunteers: queryData(snap),
        volunteersLoaded: true,
      })
    );
    const volImps = schoolVolImports(schoolUid).onSnapshot(snap =>
      this.update("imps", {
        volunteerImports: queryData(snap),
        volunteerImportsLoaded: true,
      })
    );

    this.schoolSubscriptions = [
      sch,
      acts,
      actRvws,
      cats,
      orgs,
      sups,
      vols,
      volImps,
    ];
  }

  update(tag: string, update: object) {
    const loaded = calculateCoordinatorCacheLoaded({
      ...this.state,
      ...update,
    });
    super.update(tag, { ...updateIndexes(this.state, update), loaded });
  }
}

export const getApi = lazy(() => new Api());
export const volCache = lazy(() => new VolunteerCache());
export const coordCache = lazy(() => new CoordinatorCache());

function updateIndexes(
  state: CoordinatorState,
  update: CoordinatorStateUpdate
): CoordinatorStateUpdate {
  let build = update;
  if (update.volunteersLoaded || update.volunteerImportsLoaded) {
    build = {
      ...build,
      volIndex: indexVols(
        firstNonNull(build.volunteerImports, state.volunteerImports),
        firstNonNull(build.volunteers, state.volunteers)
      ),
    };
  }
  if (update.activityReviewsLoaded) {
    build = {
      ...build,
      actRvwIndex: indexActRvws(throwIfNull(build.activityReviews)),
    };
  }
  if (update.categoriesLoaded) {
    build = {
      ...build,
      catIndex: indexCats(throwIfNull(build.categories)),
    };
  }
  if (
    update.activitiesLoaded ||
    update.volunteerImportsLoaded ||
    update.volunteersLoaded
  ) {
    const actIndex = indexActs(
      firstNonNull(build.volunteerImports, state.volunteerImports),
      firstNonNull(build.activities, state.activities)
    );
    build = { ...build, actIndex };
  }
  if (
    update.organizationsLoaded ||
    update.volunteerImportsLoaded ||
    update.volunteersLoaded
  ) {
    const orgIndex = indexOrgs(
      firstNonNull(build.volunteerImports, state.volunteerImports),
      firstNonNull(build.organizations, state.organizations)
    );
    build = { ...build, orgIndex };
  }
  if (
    update.supervisorsLoaded ||
    update.volunteerImportsLoaded ||
    update.volunteersLoaded
  ) {
    const supIndex = indexSups(
      firstNonNull(build.volunteerImports, state.volunteerImports),
      firstNonNull(build.supervisors, state.supervisors)
    );
    build = { ...build, supIndex };
  }
  return build;
}

function indexActs(
  volImps: VolunteerImportDocument[],
  acts: ActivityDocument[]
): Map<Uid, Activity> {
  const result = new Map<Uid, Activity>();
  for (let imp of volImps) {
    const volunteerUid = imp.data.email;
    for (let act of imp.data.activities) {
      result.set(act.uid, { ...activityData(act), volunteerUid });
    }
  }
  for (let act of acts) {
    result.set(act.data.uid, act.data);
  }
  return result;
}

function indexActRvws(
  actRvws: ActivityReviewDocument[]
): Map<Uid, ActivityReview> {
  const result = new Map<Uid, ActivityReview>();
  for (let actRvw of actRvws) {
    result.set(actRvw.data.uid, actRvw.data);
  }
  return result;
}

function indexCats(cats: CategoryDocument[]): Map<Uid, Category> {
  const result = new Map<Uid, Category>();
  for (let cat of cats) {
    result.set(cat.data.uid, cat.data);
  }
  return result;
}

function indexOrgs(
  volImps: VolunteerImportDocument[],
  orgs: OrganizationDocument[]
): Map<Uid, Organization> {
  const result = new Map<Uid, Organization>();
  for (let imp of volImps) {
    const volunteerUid = imp.data.email;
    for (let org of imp.data.organizations) {
      result.set(org.uid, { ...organizationData(org), volunteerUid });
    }
  }
  for (let org of orgs) {
    result.set(org.data.uid, org.data);
  }
  return result;
}

function indexSups(
  volImps: VolunteerImportDocument[],
  sups: SupervisorDocument[]
): Map<Uid, Supervisor> {
  const result = new Map<Uid, Supervisor>();
  for (let imp of volImps) {
    const volunteerUid = imp.data.email;
    for (let sup of imp.data.supervisors) {
      result.set(sup.uid, { ...supervisorData(sup), volunteerUid });
    }
  }
  for (let sup of sups) {
    result.set(sup.data.uid, sup.data);
  }
  return result;
}

function indexVols(
  volImps: VolunteerImportDocument[],
  vols: VolunteerDocument[]
): Map<Uid, Volunteer> {
  const result = new Map<Uid, Volunteer>();
  for (let imp of volImps) {
    const { name, email, classYear, schoolUid } = imp.data;
    result.set(imp.data.email, {
      uid: email,
      name,
      email,
      phone: "",
      classYear,
      schoolUid,
      acceptedMarkdowns: [],
    });
  }
  for (let vol of vols) {
    result.set(vol.data.uid, vol.data);
    result.set(vol.data.email, vol.data);
  }
  return result;
}

function defaultActivityReview(
  schoolUid: Uid,
  uid: Uid
): ActivityReviewDocument {
  return activityReviewDoc({
    uid,
    schoolUid,
    state: ReviewState.Pending,
    contents: "default value",
  });
}

function crash(message: string, info?: any): never {
  const err = new Error(message);
  error(err.message, info);
  logCrash(err, info);
  throw err;
}
